import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import Analytics from "@/components/Analytics";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/layout/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Memberships from "@/pages/Memberships";
import MembershipDetail from "@/pages/MembershipDetail";
import Classes from "@/pages/Classes";
import ClassDetail from "@/pages/ClassDetail";
import About from "@/pages/About";
import SuccessStories from "@/pages/SuccessStories";
import HowItWorks from "@/pages/HowItWorks";
import Results from "@/pages/Results";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Contact from "@/pages/Contact";
import Book from "@/pages/Book";
import Timetable from "@/pages/Timetable";
import CoachingEnquiry from "@/pages/CoachingEnquiry";
import LeadMagnet from "@/pages/LeadMagnet";
import Trial from "@/pages/Trial";
import Scorecard from "@/pages/Scorecard";
import PersonalTraining from "@/pages/PersonalTraining";
import GiftVouchers from "@/pages/GiftVouchers";
import HelpMeChoose from "@/pages/HelpMeChoose";
import GroupCoaching from "@/pages/GroupCoaching";
import GymWindermere from "@/pages/GymWindermere";
import StudioHire from "@/pages/StudioHire";
import ThankYou from "@/pages/ThankYou";
import WomensWaitlist from "@/pages/WomensWaitlist";
import FuelChapters from "@/pages/FuelChapters";
import { rememberReferrer } from "@/lib/tracking";
rememberReferrer();
import { Over40Fitness, StrengthOver50, MenopauseStrength, BeginnerGymWindermere, HateGyms } from "@/pages/Guides";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Unsubscribe from "@/pages/Unsubscribe";
import Terms from "@/pages/Terms";
import { BookingSuccess, BookingCancel, NotFound } from "@/pages/StatusPages";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const site = (el) => <Layout>{el}</Layout>;

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Analytics />
            <Toaster position="top-right" richColors closeButton />
            <Routes>
              <Route path="/" element={site(<Home />)} />
              <Route path="/memberships" element={site(<Memberships />)} />
              <Route path="/memberships/:slug" element={site(<MembershipDetail />)} />
              <Route path="/classes" element={site(<Classes />)} />
              <Route path="/classes/:slug" element={site(<ClassDetail />)} />
              <Route path="/about" element={site(<About />)} />
              <Route path="/success-stories" element={site(<SuccessStories />)} />
              <Route path="/blog" element={site(<Blog />)} />
              <Route path="/blog/:slug" element={site(<BlogDetail />)} />
              <Route path="/contact" element={site(<Contact />)} />
              <Route path="/book" element={site(<Book />)} />
              <Route path="/timetable" element={site(<Timetable />)} />
              <Route path="/coaching-enquiry" element={site(<CoachingEnquiry />)} />
              <Route path="/trial" element={<Navigate to="/30-day-trial" replace />} />
              <Route path="/30-day-trial" element={site(<Trial />)} />
              <Route path="/6-week-reset" element={<Navigate to="/30-day-trial" replace />} />
              <Route path="/thank-you" element={site(<ThankYou />)} />
              <Route path="/womens-strength-foundations" element={site(<WomensWaitlist />)} />
              <Route path="/fuel-chapters" element={site(<FuelChapters />)} />
              <Route path="/over-40-fitness" element={site(<Over40Fitness />)} />
              <Route path="/strength-over-50" element={site(<StrengthOver50 />)} />
              <Route path="/menopause-strength" element={site(<MenopauseStrength />)} />
              <Route path="/beginner-gym-windermere" element={site(<BeginnerGymWindermere />)} />
              <Route path="/gym-for-people-who-hate-gyms" element={site(<HateGyms />)} />
              <Route path="/group-coaching" element={<Navigate to="/group-personal-training-windermere" replace />} />
              <Route path="/kore-access" element={<Navigate to="/gym-windermere" replace />} />
              <Route path="/studio-rental" element={<Navigate to="/studio-hire-windermere" replace />} />
              <Route path="/how-it-works" element={site(<HowItWorks />)} />
              <Route path="/results" element={site(<Results />)} />
              <Route path="/scorecard" element={site(<Scorecard />)} />
              <Route path="/personal-training" element={site(<PersonalTraining />)} />
              <Route path="/gift-vouchers" element={site(<GiftVouchers />)} />
              <Route path="/help-me-choose" element={site(<HelpMeChoose />)} />
              {/* Local SEO landing pages */}
              <Route path="/group-personal-training-windermere" element={site(<GroupCoaching />)} />
              <Route path="/gym-windermere" element={site(<GymWindermere />)} />
              <Route path="/studio-hire-windermere" element={site(<StudioHire />)} />
              <Route path="/personal-trainer-windermere" element={<Navigate to="/group-personal-training-windermere" replace />} />
              <Route path="/gym-for-over-50s" element={<Navigate to="/group-personal-training-windermere" replace />} />
              <Route path="/personal-trainer-kendal" element={<Navigate to="/group-personal-training-windermere" replace />} />
              <Route path="/gym-ambleside" element={<Navigate to="/gym-windermere" replace />} />
              {/* Legacy / brief-mentioned URLs → redirect to canonical pages */}
              <Route path="/why-we-exist" element={<Navigate to="/about" replace />} />
              <Route path="/how-kore-works" element={<Navigate to="/memberships/kore-coached" replace />} />
              <Route path="/coached" element={<Navigate to="/memberships/kore-coached" replace />} />
              <Route path="/30-day-reset" element={<Navigate to="/trial" replace />} />
              <Route path="/free-guide" element={site(<LeadMagnet />)} />
              <Route path="/privacy" element={site(<PrivacyPolicy />)} />
              <Route path="/unsubscribe" element={site(<Unsubscribe />)} />
              <Route path="/terms" element={site(<Terms />)} />
              <Route path="/booking/success" element={site(<BookingSuccess />)} />
              <Route path="/booking/cancel" element={site(<BookingCancel />)} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="*" element={site(<NotFound />)} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
